// Tutorial hitung bertahap: satu langkah per layar, anak mengisi hitungan kecil.
import { fmt, LET } from '../core/format.js';
import { pick } from '../core/random.js';
import { layarSentuh } from '../core/dom.js';
import { mascot } from './mascot.js';
import { sfx } from './sound.js';
import { keyHTML, bacaanHTML, namaNilai, isRight } from './jawaban.js';
import { catatPembahasan } from '../game/profil.js';
import { TOPIK_BI } from '../subjects/bahasa-indonesia/topik.js';
import { kartuUntuk, bukaRumus } from './rumus.js';
import { S, panggil } from './state.js';

export function parseNum(s) {
  s = String(s).trim().replace(/\s/g, '').replace(/^rp/i, '').replace(/[°%]$/, '');
  if (!s) return NaN;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  return Number(s);
}

// Ringkasan soal untuk ditampilkan di dalam tutorial (teks, gambar, pernyataan/pilihan).
function soalRingkas(q) {
  const daftar = q.type === 'bs' ? q.statements.map((s, i) => `<li><b>${LET[i]}.</b> ${s.t}</li>`)
    : q.type === 'pg' ? q.options.map((o, i) => `<li><b>${LET[i]}.</b> ${o}</li>`)
    : q.options.map(o => `<li>${o.t}</li>`);
  const bacaan = q.bacaan ? `<details class="tut-bacaan"><summary>Teks bacaan: ${q.bacaan.judul ? q.bacaan.judul.replace(/[<>&]/g, '') : ''}</summary>${bacaanHTML(q.bacaan, { sorot: q.bukti || [] })}</details>` : '';
  return `${bacaan}<div class="qtext">${q.text}</div>${q.vis ? `<div class="vis">${q.vis}</div>` : ''}<ul class="tut-soal-list">${daftar.join('')}</ul>`;
}

// ---------- pembahasan dengan alasan setiap pilihan (soal Bahasa Indonesia, atau bank soal yang punya alasan) ----------
const alasanPg = (q, i) => q.why?.[i] || '';
const adaAlasan = q => q.type === 'pg' ? !!q.why?.some(Boolean) : (q.type === 'bs' ? q.statements : q.options).some(x => x.w);

function barisAlasan({ huruf, teks, alasan, kelas, tag }) {
  return `<li class="ta ${kelas}"><div class="ta-h">${huruf ? `<b>${huruf}.</b> ` : ''}<span>${teks}</span>${tag ? `<em>${tag}</em>` : ''}</div>${alasan ? `<p>${alasan}</p>` : ''}</li>`;
}

// Pilihan anak yang keliru (dan pasangan jawaban benarnya), atau null bila tidak ada yang keliru.
function bagianKeliru(q, sel) {
  if (sel == null) return null;
  if (q.type === 'pg') {
    if (sel === q.answer) return null;
    return [
      barisAlasan({ huruf: LET[sel], teks: q.options[sel], alasan: alasanPg(q, sel), kelas: 'no', tag: 'Pilihanmu' }),
      barisAlasan({ huruf: LET[q.answer], teks: q.options[q.answer], alasan: alasanPg(q, q.answer), kelas: 'ok', tag: 'Jawaban benar' }),
    ];
  }
  if (q.type === 'mcma') {
    const out = [];
    q.options.forEach((o, i) => {
      const s = sel.has?.(i);
      if (s && !o.v) out.push(barisAlasan({ teks: o.t, alasan: o.w, kelas: 'no', tag: 'Kamu pilih, padahal bukan jawaban' }));
      if (!s && o.v) out.push(barisAlasan({ teks: o.t, alasan: o.w, kelas: 'miss', tag: 'Seharusnya ikut dipilih' }));
    });
    return out.length ? out : null;
  }
  const out = q.statements.map((x, i) => sel[i] === x.v ? '' : barisAlasan({ huruf: LET[i], teks: x.t, alasan: x.w, kelas: 'no', tag: `Jawabannya ${namaNilai(q, x.v).toUpperCase()}` })).filter(Boolean);
  return out.length ? out : null;
}

function semuaAlasan(q) {
  if (q.type === 'pg') return q.options.map((o, i) => barisAlasan({ huruf: LET[i], teks: o, alasan: alasanPg(q, i), kelas: i === q.answer ? 'ok' : 'x', tag: i === q.answer ? 'Benar' : 'Salah' }));
  if (q.type === 'mcma') return q.options.map(o => barisAlasan({ teks: o.t, alasan: o.w, kelas: o.v ? 'ok' : 'x', tag: o.v ? 'Benar' : 'Salah' }));
  return q.statements.map((x, i) => barisAlasan({ huruf: LET[i], teks: x.t, alasan: x.w, kelas: x.v ? 'ok' : 'x', tag: namaNilai(q, x.v) }));
}

/** Langkah pembahasan lengkap: langkah dari soal, lalu bukti di teks, pilihan yang keliru, dan semua alasan. */
export function langkahLengkap(q, sel) {
  const steps = [...q.steps];
  if (q.bacaan && q.bukti?.length) steps.push({ t: '<b>Cari buktinya di teks.</b> Bagian yang disorot kuning adalah petunjuk jawabannya. Baca pelan-pelan, ya.', v: bacaanHTML(q.bacaan, { sorot: q.bukti, hanyaBukti: true }), lebar: true });
  if (adaAlasan(q)) {
    const keliru = bagianKeliru(q, sel);
    if (keliru) steps.push({ t: q.type === 'pg' ? '<b>Kenapa pilihanmu belum tepat?</b> Bandingkan pilihanmu dengan jawaban yang benar.' : '<b>Bagian mana yang belum tepat?</b>', v: `<ul class="tut-alasan">${keliru.join('')}</ul>`, lebar: true });
    steps.push({ t: `<b>Periksa setiap pilihan.</b> Ini alasan kenapa setiap ${q.type === 'bs' ? 'pernyataan benar atau salah' : 'pilihan benar atau salah'}.`, v: `<ul class="tut-alasan">${semuaAlasan(q).join('')}</ul>`, lebar: true });
  }
  return steps;
}

let seq = 0;
/**
 * @param {object} q soal
 * @param {HTMLElement} host wadah tutorial
 * @param {{tampilSoal?: boolean}} opsi tampilSoal: buka panel soal sejak awal
 */
export function tutor(q, host, { tampilSoal = false, sel = null } = {}) {
  const tips = TOPIK_BI[q.gen]?.tips;
  const steps = [...langkahLengkap(q, sel), { t: `<b>Kesimpulan.</b> Jawaban yang benar: ${keyHTML(q)}`, tips, final: true }];
  const bahas = !!(q.bacaan || tips);
  const st = steps.map(s => ({ done: !s.ask, good: null, tries: 0, msg: '' }));
  const uid = 't' + (++seq), maxTries = S.konten?.config?.kuis?.percobaanPerLangkah || 2;
  const adaRumus = kartuUntuk(q.rumus).length > 0;
  const nama = panggil();
  let i = 0, maxI = 0, tercatat = false;
  // Pembahasan soal yang salah sampai langkah kesimpulan dihitung untuk misi & lencana "Pantang Menyerah".
  const salah = sel != null && !isRight(q, sel);
  host.innerHTML = `<div class="tut"><div class="tut-top"><span class="tut-title">${mascot('think', 34)}<b>Ayo ${nama}, kita ${bahas ? 'bahas' : 'hitung'} bareng!</b></span><span class="tut-tools"><button class="btn btn-sm" data-soal type="button" aria-expanded="${tampilSoal}">${tampilSoal ? 'Sembunyikan soal' : 'Lihat soal'}</button>${adaRumus ? '<button class="btn btn-sm btn-sky" data-rumus type="button">Kartu rumus</button>' : ''}</span><div class="dots">${steps.map((s, k) => `<button class="dot-b" data-k="${k}" type="button" aria-label="Langkah ${k + 1}"></button>`).join('')}</div></div><div class="tut-soal scroll"${tampilSoal ? '' : ' hidden'}>${soalRingkas(q)}</div><div class="tut-card scroll" aria-live="polite"></div><div class="tut-nav"><button class="btn btn-sm" data-nav="-1" type="button">← Kembali</button><span class="tut-count"></span><button class="btn btn-sm btn-sun" data-nav="1" type="button">Lanjut →</button></div></div>`;
  const card = host.querySelector('.tut-card'), prev = host.querySelector('[data-nav="-1"]'), nxt = host.querySelector('[data-nav="1"]'), cnt = host.querySelector('.tut-count'), dots = [...host.querySelectorAll('.dot-b')];
  const go = k => { i = k; maxI = Math.max(maxI, i); render(); };
  prev.onclick = () => { if (i > 0) go(i - 1); };
  nxt.onclick = () => { if (st[i].done && i < steps.length - 1) go(i + 1); };
  dots.forEach(d => d.onclick = () => { const k = +d.dataset.k; if (k <= maxI) go(k); });
  host.querySelector('[data-rumus]')?.addEventListener('click', () => bukaRumus(q.rumus));
  const tSoal = host.querySelector('[data-soal]'), pSoal = host.querySelector('.tut-soal');
  tSoal.onclick = () => { pSoal.hidden = !pSoal.hidden; tSoal.textContent = pSoal.hidden ? 'Lihat soal' : 'Sembunyikan soal'; tSoal.setAttribute('aria-expanded', !pSoal.hidden); };

  function render() {
    const s = steps[i], x = st[i], askId = `${uid}-a${i}`;
    let ask = '';
    const pilihan = s.ask && Array.isArray(s.ask.opsi);
    const jawabanAsk = s.ask && (pilihan ? s.ask.opsi[s.ask.a] : fmt(s.ask.a) + (s.ask.u ? ' ' + s.ask.u : ''));
    if (s.ask) {
      ask = x.done
        ? `<div class="ask ask-done${x.good ? '' : ' miss'}"><span class="ask-q">${s.ask.q}</span> → <b>${jawabanAsk}</b><div class="ask-msg ${x.good ? 'ok' : 'no'}">${x.good ? x.msg : `Jawabannya ${jawabanAsk}. Tidak apa-apa, ${nama}, ayo lanjut!`}</div></div>`
        : pilihan
          ? `<div class="ask ask-pilih"><span class="ask-q">${s.ask.q}</span><div class="ask-opsi">${s.ask.opsi.map((o, k) => `<button class="btn btn-sm ask-b" type="button" data-k="${k}"><b>${LET[k]}.</b> ${o}</button>`).join('')}</div><button class="btn btn-sm btn-ghost" type="button" data-act="tell">Kasih tahu aku</button><div class="ask-msg" aria-live="polite"></div></div>`
          : `<div class="ask"><label class="ask-q" for="${askId}">${s.ask.q}</label><input id="${askId}" inputmode="decimal" autocomplete="off">${s.ask.u ? `<span>${s.ask.u}</span>` : ''}<button class="btn btn-sm btn-sky" type="button" data-act="cek">Cek</button><button class="btn btn-sm btn-ghost" type="button" data-act="tell">Kasih tahu aku</button><div class="ask-msg" aria-live="polite"></div></div>`;
    }
    card.innerHTML = `<div class="step${s.final ? ' final' : ''}"><div class="step-n">${s.final ? '★' : i + 1}</div><div class="step-b">${s.t ? `<p>${s.t}</p>` : ''}${s.v ? `<div class="vis${s.lebar ? ' vis-teks' : ''}">${s.v}</div>` : ''}${ask}${s.after && x.done ? `<div class="step-after">${s.after}</div>` : ''}${s.tips ? `<div class="tut-tips"><b>Tips untuk soal sejenis</b><p>${s.tips.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/\*(.+?)\*/g, '<i>$1</i>')}</p></div>` : ''}${s.final ? `<p class="muted" style="font-weight:800">Sekarang ${nama} sudah tahu caranya!</p>` : ''}</div></div>`;
    card.scrollTop = 0;
    if (s.final && salah && !tercatat) { tercatat = true; catatPembahasan(); }
    prev.disabled = i === 0; nxt.hidden = i === steps.length - 1; nxt.disabled = !x.done;
    cnt.textContent = `Langkah ${i + 1} dari ${steps.length}`;
    dots.forEach((d, k) => { d.className = 'dot-b' + (k === i ? ' on' : k <= maxI ? ' seen' : ''); d.disabled = k > maxI; });
    if (s.ask && !x.done) {
      const box = card.querySelector('.ask'), inp = box.querySelector('input'), msg = box.querySelector('.ask-msg');
      const fin = good => { x.done = true; x.good = good; x.msg = pick(['Betul!', `Tepat sekali, ${nama}!`, 'Pintar!', `Yap, benar, ${nama}!`, `${nama} hebat!`]); good ? sfx.ok() : sfx.no(); render(); setTimeout(() => nxt.hidden || nxt.focus({ preventScroll: true }), 20); };
      box.querySelector('[data-act="tell"]').onclick = () => fin(false);
      if (pilihan) {
        box.querySelectorAll('.ask-b').forEach(b => b.onclick = () => {
          if (+b.dataset.k === s.ask.a) return fin(true);
          x.tries++;
          b.disabled = true; b.classList.add('salah');
          if (x.tries >= maxTries) return fin(false);
          msg.className = 'ask-msg no'; msg.textContent = `Belum tepat, ${nama}. Baca lagi teksnya, lalu coba pilihan lain.`; sfx.no();
        });
        return;
      }
      const cek = () => {
        const v = parseNum(inp.value);
        if (isNaN(v)) { msg.className = 'ask-msg no'; msg.textContent = 'Tulis jawabannya dengan angka, ya.'; return; }
        if (Math.abs(v - s.ask.a) < 1e-6) return fin(true);
        x.tries++;
        if (x.tries >= maxTries) return fin(false);
        msg.className = 'ask-msg no'; msg.textContent = `Hampir, ${nama}! Coba hitung sekali lagi.`; inp.select(); sfx.no();
      };
      box.querySelector('[data-act="cek"]').onclick = cek;
      inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); cek(); } });
      if (!layarSentuh()) setTimeout(() => inp.focus({ preventScroll: true }), 40);
    }
  }
  render();
  return { langkahAktif: () => i };
}
